import { msg } from "@lingui/core/macro";
import { Show } from "solid-js";
import { useDrizzle } from "@/hooks/use-drizzle";
import type { Todo } from "@listto/powersync";
import { cn, updateTodoMutation } from "@listto/shared";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar, Check, List as ListIcon, Repeat, Star, Sun, Trash } from "lucide-solid";
import { useI18n } from "@/providers/i18n-provider";
import {
  OPTIMISTIC_TIMEOUT_MS,
  setOptimisticCompleted,
  setOptimisticImportant,
  VISUAL_DELAY_MS,
} from "./todo-item-optimistic";

interface TodoMetadataBadgesProps {
  todo: Todo;
  listId: string;
  listName: string | undefined;
  isOverdue: boolean;
  hasBadges: boolean;
}

export function TodoMetadataBadges(props: TodoMetadataBadgesProps) {
  const { t } = useI18n();

  return (
    <Show when={props.hasBadges}>
      <div class="flex flex-wrap items-center gap-x-2.5 gap-y-1 mt-1.5">
        <Show when={props.todo.due_date}>
          {(date) => (
            <div
              class={cn(
                "flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold tracking-tight transition-colors",
                props.isOverdue
                  ? "bg-danger/10 text-danger border border-danger/20"
                  : "bg-muted/30 text-muted-foreground border border-transparent",
              )}
            >
              <Calendar class="h-3 w-3" />
              <span>
                {new Date(date()).toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                })}
              </span>
            </div>
          )}
        </Show>

        <Show when={props.todo.my_day_date}>
          <div class="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-500/10 text-amber-500 dark:text-amber-400 text-[10px] font-bold uppercase tracking-wider border border-amber-500/20 shrink-0">
            <Sun class="h-3 w-3 shrink-0" />
            <span>{t(msg`My Day`)}</span>
          </div>
        </Show>

        <Show when={props.todo.recurrence_rule}>
          <div class="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-primary/5 text-primary/70 text-[10px] font-semibold border border-primary/10">
            <Repeat class="h-3 w-3" />
          </div>
        </Show>

        <Show when={(props.listId === "all" || props.listId === "planned") && props.listName}>
          <div class="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-muted/40 text-muted-foreground text-[10px] font-bold uppercase tracking-wider border border-transparent max-w-[120px] sm:max-w-[180px]">
            <ListIcon class="h-2.5 w-2.5 shrink-0" />
            <span class="truncate">{props.listName}</span>
          </div>
        </Show>
      </div>
    </Show>
  );
}

interface TodoKeyboardHintsProps {
  isKeyboardFocused?: boolean;
}

export function TodoKeyboardHints(props: TodoKeyboardHintsProps) {
  const { t } = useI18n();

  return (
    <Show when={props.isKeyboardFocused}>
      <div class="hidden lg:flex items-center gap-2.5 mr-1.5 animate-in fade-in slide-in-from-right-2 duration-200 select-none flex-nowrap shrink-0">
        <div
          class="flex items-center gap-1 cursor-help"
          title={t(msg`Press Enter to open details for this task`)}
        >
          <kbd class="h-4.5 min-w-[15px] inline-flex items-center justify-center rounded border border-border/60 bg-background px-1 font-mono text-[8px] font-bold text-foreground/80 shadow-xs">
            ↵
          </kbd>
          <span class="text-[7.5px] font-extrabold text-muted-foreground/80 tracking-tight uppercase">
            {t(msg`Open`)}
          </span>
        </div>
        <div
          class="flex items-center gap-1 cursor-help"
          title={t(msg`Press Space or E to complete this task`)}
        >
          <kbd class="h-4.5 min-w-[15px] inline-flex items-center justify-center rounded border border-border/60 bg-background px-1 font-mono text-[8px] font-bold text-foreground/80 shadow-xs">
            Space
          </kbd>
          <span class="text-[7.5px] font-extrabold text-muted-foreground/80 tracking-tight uppercase">
            {t(msg`Done`)}
          </span>
        </div>
        <div
          class="flex items-center gap-1 cursor-help"
          title={t(msg`Press I or S to star this task as important`)}
        >
          <kbd class="h-4.5 min-w-[15px] inline-flex items-center justify-center rounded border border-border/60 bg-background px-1 font-mono text-[8px] font-bold text-foreground/80 shadow-xs">
            I
          </kbd>
          <span class="text-[7.5px] font-extrabold text-muted-foreground/80 tracking-tight uppercase">
            {t(msg`Star`)}
          </span>
        </div>
        <div
          class="flex items-center gap-1 cursor-help"
          title={t(msg`Press Delete or Backspace to delete this task`)}
        >
          <kbd class="h-4.5 min-w-[15px] inline-flex items-center justify-center rounded border border-border/60 bg-background px-1 font-mono text-[8px] font-bold text-foreground/80 shadow-xs">
            Del
          </kbd>
          <span class="text-[7.5px] font-extrabold text-muted-foreground/80 tracking-tight uppercase">
            {t(msg`Delete`)}
          </span>
        </div>
      </div>
    </Show>
  );
}

interface TodoItemUnderlayProps {
  tx: () => number;
  commitThreshold: number;
  isCompleted: boolean;
}

const UNDERLAY_MIN_OPACITY = 0.2;
const UNDERLAY_OPACITY_RANGE = 0.8;
const UNDERLAY_MIN_SCALE = 0.6;
const UNDERLAY_MAX_SCALE = 1.2;
const UNDERLAY_SCALE_RANGE = 0.4;

export function TodoItemUnderlay(props: TodoItemUnderlayProps) {
  const { t } = useI18n();
  const tx = () => props.tx();
  const threshold = () => props.commitThreshold;

  const leftOpacity = () => {
    const currentTx = tx();
    const currentThreshold = threshold();
    if (currentTx <= 0) {
      return 0;
    }
    if (currentTx >= currentThreshold) {
      return 1;
    }
    return UNDERLAY_MIN_OPACITY + (currentTx / currentThreshold) * UNDERLAY_OPACITY_RANGE;
  };

  const rightOpacity = () => {
    const currentTx = tx();
    const currentThreshold = threshold();
    const absTx = -currentTx;
    if (currentTx >= 0) {
      return 0;
    }
    if (absTx >= currentThreshold) {
      return 1;
    }
    return UNDERLAY_MIN_OPACITY + (absTx / currentThreshold) * UNDERLAY_OPACITY_RANGE;
  };

  return (
    <div class="absolute inset-0 z-0 flex items-center justify-between rounded-[var(--radius-xl)] pointer-events-none overflow-hidden select-none">
      {/* Izquierda: Completar (Fondo Verde) */}
      <div
        class={cn(
          "absolute inset-y-0 left-0 flex items-center pl-6 text-white transition-all duration-300 rounded-l-[var(--radius-xl)]",
          tx() > threshold()
            ? "bg-emerald-500 shadow-[inset_-20px_0_30px_rgba(0,0,0,0.15)]"
            : "bg-emerald-600/90",
        )}
        style={{
          opacity: leftOpacity(),
          width: `${Math.max(0, tx())}px`,
        }}
      >
        <div class="flex items-center gap-2">
          <Check
            class={cn(
              "h-5.5 w-5.5 transition-all duration-200 ease-out",
              tx() > threshold()
                ? "scale-125 rotate-6 text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.4)]"
                : "text-white/80",
            )}
            style={{
              transform:
                tx() <= threshold()
                  ? `scale(${Math.min(
                      UNDERLAY_MAX_SCALE,
                      UNDERLAY_MIN_SCALE + (tx() / threshold()) * UNDERLAY_SCALE_RANGE,
                    )})`
                  : undefined,
            }}
          />
          <span
            class={cn(
              "text-[12px] font-bold tracking-wide transition-all duration-200 ease-[var(--ease-spring-pop)]",
              tx() > threshold()
                ? "opacity-100 translate-x-0 scale-100"
                : "opacity-0 -translate-x-2 scale-90",
            )}
          >
            {props.isCompleted ? t(msg`Reactivar`) : t(msg`Completar`)}
          </span>
        </div>
      </div>

      {/* Derecha: Eliminar (Fondo Rojo) */}
      <div
        class={cn(
          "absolute inset-y-0 right-0 flex items-center justify-end pr-6 text-white transition-all duration-300 rounded-r-[var(--radius-xl)]",
          tx() < -threshold()
            ? "bg-rose-500 shadow-[inset_20px_0_30px_rgba(0,0,0,0.15)]"
            : "bg-rose-600/90",
        )}
        style={{
          opacity: rightOpacity(),
          width: `${Math.max(0, -tx())}px`,
        }}
      >
        <div class="flex items-center gap-2">
          <span
            class={cn(
              "text-[12px] font-bold tracking-wide transition-all duration-200 ease-[var(--ease-spring-pop)]",
              tx() < -threshold()
                ? "opacity-100 translate-x-0 scale-100"
                : "opacity-0 translate-x-2 scale-90",
            )}
          >
            {t(msg`Eliminar`)}
          </span>
          <Trash
            class={cn(
              "h-5.5 w-5.5 transition-all duration-200 ease-out",
              tx() < -threshold()
                ? "scale-125 -rotate-6 text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.4)]"
                : "text-white/80",
            )}
            style={{
              transform:
                tx() >= -threshold()
                  ? `scale(${Math.min(
                      UNDERLAY_MAX_SCALE,
                      UNDERLAY_MIN_SCALE + (-tx() / threshold()) * UNDERLAY_SCALE_RANGE,
                    )})`
                  : undefined,
            }}
          />
        </div>
      </div>
    </div>
  );
}

interface TodoItemCheckboxButtonProps {
  todo: Todo;
  isSelectionMode: boolean;
  isSelected: boolean;
  isCompleted: boolean;
  onToggleSelection: (id: string) => void;
  onToggle: (todo: Todo) => void;
}

export function TodoItemCheckboxButton(props: TodoItemCheckboxButtonProps) {
  const { t } = useI18n();

  return (
    <button
      type="button"
      class={cn(
        "shrink-0 -my-1 -ml-2 h-10 w-10 pl-2 pr-1 flex items-center justify-center transition-[transform,background-color] duration-300 ease-[var(--ease-spring-pop)] active:scale-90 hover:scale-105 bg-transparent border-none p-0 cursor-pointer rounded-xl focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary focus-visible:outline-none focus:outline-none",
        props.isSelectionMode ? "hover:bg-primary/10" : "hover:bg-accent/50",
      )}
      onClick={(e) => {
        e.stopPropagation();
        if (props.isSelectionMode) {
          props.onToggleSelection(props.todo.id);
        } else {
          const nextVal = !props.isCompleted;
          setOptimisticCompleted(props.todo.id, nextVal);

          const targetId = props.todo.id;
          setTimeout(() => {
            setOptimisticCompleted(targetId, undefined);
          }, OPTIMISTIC_TIMEOUT_MS);

          setTimeout(() => {
            props.onToggle(props.todo);
          }, VISUAL_DELAY_MS);
        }
      }}
      aria-label={t(msg`Toggle completion`)}
    >
      <Checkbox
        checked={props.isSelectionMode ? props.isSelected : props.isCompleted}
        onChange={() => {
          /* empty */
        }} // Handled by button onClick for better hit area
        class="h-4.5 w-4.5 pointer-events-none"
        tabIndex={-1}
      />
    </button>
  );
}

interface TodoItemStarButtonProps {
  todo: Todo;
  isImportant: boolean;
}

export function TodoItemStarButton(props: TodoItemStarButtonProps) {
  const { t } = useI18n();
  const drizzle = useDrizzle();

  return (
    <button
      type="button"
      class={cn(
        "flex items-center justify-center h-10 w-10 -my-1 -mr-2 pr-2 pl-1 rounded-xl transition-[transform,background-color] duration-300 ease-[var(--ease-spring-pop)] active:scale-90 hover:scale-105 border-none bg-transparent p-0 cursor-pointer focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary focus-visible:outline-none focus:outline-none",
        props.isImportant ? "bg-primary/5 shadow-inner scale-105" : "hover:bg-primary/10",
      )}
      onClick={(e) => {
        e.stopPropagation();
        const nextVal = !props.isImportant;
        setOptimisticImportant(props.todo.id, nextVal);

        const targetId = props.todo.id;
        setTimeout(() => {
          setOptimisticImportant(targetId, undefined);
        }, OPTIMISTIC_TIMEOUT_MS);

        const dbInstance = drizzle();
        if (dbInstance) {
          setTimeout(() => {
            void updateTodoMutation(dbInstance, props.todo.id, {
              important: nextVal ? 1 : 0,
            });
          }, VISUAL_DELAY_MS);
        }
      }}
      aria-label={t(msg`Toggle important`)}
    >
      <Star
        class={cn(
          "h-4 w-4 pointer-events-none transition-[transform,colors] duration-300 ease-[var(--ease-spring-pop)]",
          props.isImportant
            ? "fill-primary text-primary scale-110 drop-shadow-[0_0_8px_rgba(var(--accent-rgb),0.3)]"
            : "text-muted-foreground/20 group-hover:text-primary/40",
        )}
      />
    </button>
  );
}
